import { useEffect } from 'react';
import { hasAnalyticsConsent } from '../utils/cookieUtils';
import { loadPostHogScript } from '../utils/scriptLoadingUtils';

// Prevents concurrent initPostHog() calls from each loading and calling init() independently.
let _initializing = false;

/**
 * Initialize PostHog analytics with GDPR-compliant configuration.
 * @returns {Promise<boolean>} True if PostHog was initialized
 */
export const initPostHog = async () => {
  if (!hasAnalyticsConsent()) return false;
  if (window.posthog?.__loaded) return true;

  // Block concurrent calls — prevents double-init while the library is loading.
  if (_initializing) return false;
  _initializing = true;

  if (!window.posthog) {
    try {
      await loadPostHogScript();
    } catch {
      _initializing = false;
      return false;
    }
  }

  if (!window.posthog) {
    _initializing = false;
    return false;
  }

  if (window.posthog.__loaded) {
    _initializing = false;
    return true;
  }

  try {
    window.posthog.init('phc_4Ewgs7MRbcN0bJK5eLIh6HjDMSJpbmiyY7fwIqsyTv1', {
      api_host: 'https://eu.i.posthog.com',
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: true,
      persistence: 'localStorage+cookie',
      persistence_name: 'ph_cap_explorer',
      respect_dnt: true,
      opt_out_capturing_by_default: false,
      opt_out_persistence_by_default: false,
      cross_subdomain_cookie: false,
      secure_cookie: true,
      session_recording: {
        maskAllInputs: true,
      },
      loaded: (posthog) => {
        posthog.__loaded = true;
        _initializing = false;
      },
    });

    return true;
  } catch {
    _initializing = false;
    return false;
  }
};

/**
 * Disable PostHog and clear all storage.
 * Synchronous — disables persistence first so PostHog cannot write new
 * cookies or localStorage entries before the object is destroyed.
 */
export const disablePostHog = () => {
  _initializing = false;

  if (!window.posthog) {
    return;
  }

  try {
    // Disable persistence before anything else so no further writes occur
    if (typeof window.posthog.set_config === 'function') {
      window.posthog.set_config({ disable_persistence: true });
    }

    // Opt out of capturing for the currently running instance (stops session recording too)
    if (typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }

    if (typeof window.posthog.reset === 'function') {
      window.posthog.reset(true);
    }

    window.posthog.__loaded = false;
    delete window.posthog;
  } catch {
    delete window.posthog;
  }
};

/**
 * Get all PostHog storage keys for cleanup
 * @returns {Object} Object with localStorage and sessionStorage arrays
 */
export const getPostHogStorageKeys = () => {
  const projectKey = 'phc_4Ewgs7MRbcN0bJK5eLIh6HjDMSJpbmiyY7fwIqsyTv1';
  const patterns = [
    projectKey, 
    'ph_cap_explorer', 
    '__ph_opt_in_out', 
    'ph_', 
    'posthog',
    'ph_current'
  ];

  const localStorageKeys = [];
  Object.keys(localStorage).forEach(key => {
    if (patterns.some(pattern => key.includes(pattern))) {
      localStorageKeys.push(key);
    }
  });

  const sessionStorageKeys = [];
  Object.keys(sessionStorage).forEach(key => {
    if (patterns.some(pattern => key.includes(pattern))) {
      sessionStorageKeys.push(key);
    }
  });

  return {
    localStorage: localStorageKeys,
    sessionStorage: sessionStorageKeys
  };
};

/**
 * Initialize PostHog based on user consent
 */
export const usePostHog = () => {
  useEffect(() => {
    const hasConsent = hasAnalyticsConsent();
    
    if (hasConsent) {
      initPostHog();
    }
  }, []);
};

/**
 * Identify user in PostHog after login
 * @param {string} userId - Unique user identifier
 * @param {Object} userProperties - Additional user properties
 */
export const identifyPostHogUser = (userId, userProperties = {}) => {
  const hasConsent = hasAnalyticsConsent();

  if (!hasConsent || !window.posthog) {
    return;
  }

  try {
    window.posthog.identify(userId, userProperties);
  } catch (error) {
    // Silent fail
  }
};

/**
 * Track custom events in PostHog
 * @param {string} eventName - Name of the event
 * @param {Object} properties - Event properties
 */
export const trackPostHogEvent = (eventName, properties = {}) => {
  const hasConsent = hasAnalyticsConsent();

  if (!hasConsent || !window.posthog) {
    return;
  }

  try {
    window.posthog.capture(eventName, properties);
  } catch (error) {
    // Silent fail
  }
};